import { Button } from 'primereact/button';
import {
  CartItemInterface,
  useRemoveItemFromCartMutation,
  useUpdateCartItemsMutation,
} from 'graphql/generated/magentoApi';
import { InputNumber } from 'primereact/inputnumber';
import { ReactElement, useEffect, useState } from 'react';
import { useCartContext } from 'CartContext';
import { Link, useNavigate } from 'react-router-dom';
import useDebounce from 'hooks/useDebounce';
import ReactGA from 'react-ga4';
import './CartItemList.scss';
import useViewport from 'hooks/useViewport';

type CartItemListProps = {
  updateCart: boolean;
  cleanState: () => void;
};

export default function CartItemList({
  updateCart,
  cleanState,
}: CartItemListProps): ReactElement {
  const { cartId, items, updateItems } = useCartContext();
  const [{ fetching, data }, removeItem] = useRemoveItemFromCartMutation();
  const [, updateCartItems] = useUpdateCartItemsMutation();
  const [hasToUpdate, setHasToUpdate] = useState(false);
  const navigate = useNavigate();
  const [idToRemove, setIdToRemove] = useState<string | undefined>();
  const { width } = useViewport();
  const isMobileWidth = width < 576;
  const [isMobile, setIsMobile] = useState(isMobileWidth);

  useEffect(() => {
    if (isMobileWidth !== isMobile) {
      setIsMobile(isMobileWidth);
    }
  }, [width, isMobile, isMobileWidth]);

  useEffect(() => {
    if (updateCart) {
      updateCartItems({
        input: {
          cart_id: cartId,
          cart_items: items.map(item => ({
            cart_item_uid: item.uid,
            quantity: item.quantity,
          })),
        },
      });
      navigate('/checkout');
    }
    return () => {
      cleanState();
    };
  }, [cartId, cleanState, items, navigate, updateCart, updateCartItems]);

  useEffect(() => {
    if (hasToUpdate && !fetching && data) {
      setTimeout(() => {
        updateItems(
          (data.removeItemFromCart?.cart
            .items as unknown as CartItemInterface[]) || [],
        );
        setHasToUpdate(false);
      }, 300);
    }
  }, [hasToUpdate, fetching, data, updateItems]);

  const syncCart = () => {
    updateCartItems({
      input: {
        cart_id: cartId,
        cart_items: items.map(item => ({
          cart_item_uid: item.uid,
          quantity: item.quantity,
        })),
      },
    });
  };

  const resetDebounce = useDebounce(syncCart, 3000);

  const onClickXBtn = (uid: string) => {
    if (!fetching && !hasToUpdate) {
      setIdToRemove(uid);
      removeItem({
        input: {
          cart_id: cartId,
          cart_item_uid: uid,
        },
      });
      setHasToUpdate(true);
      ReactGA.event({
        category: 'ecommerce',
        action: 'cart_item_deleted',
        label: 'Cart Item Deleted',
      });
    }
  };

  const onClickQtyBtn = (uid: string, quantity: number) => {
    const newItems = items.map(item =>
      item.uid === uid ? { ...item, quantity } : item,
    );
    updateItems(newItems);
    resetDebounce();
  };

  useEffect(() => {
    const QuantityButtons = Array.from(
      document.querySelectorAll('.p-inputnumber-button'),
    );
    QuantityButtons.forEach(QuantityButton => {
      QuantityButton.setAttribute('aria-label', 'Change Quantity');
    });
  });

  useEffect(() => {
    const QuantityInputs = Array.from(
      document.querySelectorAll(
        '.p-inputtext.p-component.p-filled.p-inputnumber-input',
      ),
    );
    QuantityInputs.forEach(QuantityInput => {
      QuantityInput.setAttribute('aria-label', 'Change Quantity Input');
    });
  });

  type TypeItem = {
    __typename?: string;
  };

  type NewCartItemInterface = CartItemInterface & TypeItem;

  const itemRow = (item: NewCartItemInterface): ReactElement => {
    const subscriptionText =
      item.product.is_subscription === 1
        ? `${item.product.name}\nMonthly Subscription`
        : `${item.product.name}`;
    const subscriptionPricing =
      item.product.is_subscription === 1
        ? `$${(
            item.quantity *
            Number(item.product.price_range.maximum_price.final_price.value)
          ).toFixed(2)}/mo`
        : `$${(
            item.quantity *
            Number(item.product.price_range.maximum_price.final_price.value)
          ).toFixed(2)}`;
    if (isMobile) {
      return (
        <div key={item.uid} className="grid m-0 py-2">
          <Link to={`/product/${item.product.sku}`} className="col-3 sm:col-4">
            <picture className="cart-item__image">
              <img
                alt={String(item.product.name) || ''}
                src={String(item.product.image?.url) || ''}
              />
            </picture>
          </Link>
          <div className="col-9 sm:col-8 flex flex-column justify-content-between">
            <div className="mb-4">
              <p className="m-0 font-bold" style={{ whiteSpace: 'pre-line' }}>
                {subscriptionText}
              </p>
              <p className="m-0">{subscriptionPricing}</p>
            </div>
            <div className="flex justify-content-between">
              <Button
                onClick={() => onClickXBtn(item.uid)}
                className="cart-item__delete cursor-pointer mr-1"
                loading={item.uid === idToRemove && fetching}
                aria-label={`Delete Product ${item.product.name}`}
                icon="pi pi-trash"
              />
              <InputNumber
                className="cart-item__quantity--mobile c-button-gray"
                aria-label="Item Quantity"
                value={item.quantity}
                showButtons
                min={1}
                max={99}
                buttonLayout="horizontal"
                incrementButtonIcon="pi pi-plus"
                decrementButtonIcon="pi pi-minus"
                disabled={
                  !item.product.name.toLowerCase().includes('donation') &&
                  item.__typename === 'VirtualCartItem'
                }
                onValueChange={e => onClickQtyBtn(item.uid, e.value)}
              />
            </div>
          </div>
        </div>
      );
    }
    return (
      <div key={item.uid} className="grid m-0 p-2 checkout-item--border">
        <Link to={`/product/${item.product.sku}`} className="col-2">
          <picture className="cart-item__image">
            <img
              alt={String(item.product.name) || ''}
              src={String(item.product.image?.url) || ''}
            />
          </picture>
        </Link>
        <div className="col-4 flex align-items-center">
          <p className="m-0 text-800" style={{ whiteSpace: 'pre-line' }}>
            {subscriptionText}
          </p>
        </div>
        <div className="col-2 flex justify-content-center align-items-center">
          <InputNumber
            className="cart-item__quantity--desktop c-button-gray h-10"
            aria-label="Item Quantity"
            value={item.quantity}
            showButtons
            buttonLayout="horizontal"
            min={1}
            max={99}
            incrementButtonIcon="pi pi-plus"
            decrementButtonIcon="pi pi-minus"
            disabled={
              !item.product.name.toLowerCase().includes('crwdunit') &&
              !item.product.name.toLowerCase().includes('donation') &&
              item.__typename === 'VirtualCartItem'
            }
            onValueChange={e => onClickQtyBtn(item.uid, e.value)}
          />
        </div>
        <div className="col-2 flex justify-content-center align-items-center">
          <Button
            onClick={() => onClickXBtn(item.uid)}
            className="cart-item__delete cursor-pointer"
            loading={item.uid === idToRemove && fetching}
            aria-label={`Delete Product ${item.product.name}`}
            icon="pi pi-trash"
          />
        </div>
        <div className="col-2 flex justify-content-end align-items-center">
          <span className="text-800 font-bold">{subscriptionPricing}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full border-1 border-300 mb-2 cart-card">
      <div className="border-bottom-1 border-300 text-800 font-bold cart-card--header p-0 sm:p-2">
        <div className="grid hidden sm:flex m-0">
          <div className="col-2">Image</div>
          <div className="col-4">Name</div>
          <div className="col-2 text-center">Quantity</div>
          <div className="col-2" />
          <div className="col-2 text-right">Total</div>
        </div>
        <div className="sm:hidden font-bold px-2 py-1">Order summary</div>
      </div>
      <div
        className="cart-item__list p-0"
        style={{ maxHeight: isMobile ? '300px' : '400px', overflowY: 'auto' }}
      >
        {items.map(item => itemRow(item))}
      </div>
    </div>
  );
}
